import { getFiltersMap } from '../components/Table/utils';
import { BuildResultsEndpoint, FilterParam } from '../types';
import buildBaseEndpoint from './buildBaseEndpoint';
import { decodeData } from './encodeUtils';
import buildNumericFilters from './buildNumericFilters';

const expandSourceValue = (
  sourceValue: string,
  tab: string,
  restrictSources: string | false | null = null,
): string => {
  return sourceValue;
};

const buildResultEndpoint = ({
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  mappingContent,
  pageNum,
  pageSize,
  orderByParam,
  order,
  defaultFilterValue,
  sourceWeights,
}: BuildResultsEndpoint) => {
  const url = window.location.search;
  const p = new URLSearchParams(url);
  const filterToken = p.get('f');
  const decodedFilters = decodeData(filterToken as string);

  const relevantFilters =
    decodedFilters && decodedFilters.length > 0
      ? decodedFilters.filter(f => f.isEvidence === undefined || f.isEvidence === isEvidence)
      : [];

  let allFilters: any[] =
    relevantFilters.length > 0
      ? (() => {
          const defaultByName = new Map((defaultFilterValue || []).map((f: any) => [f.name, f]));
          const merged = (defaultFilterValue || []).map((f: any) => {
            const fromUrl = relevantFilters.find((r: any) => r.name === f.name);
            if (fromUrl) return { ...f, ...fromUrl, type: f.type || fromUrl.type };
            return f;
          });
          relevantFilters.forEach((r: any) => {
            if (!defaultByName.has(r.name)) merged.push({ ...r, type: r.type || 'string' });
          });
          return merged;
        })()
      : defaultFilterValue || [];

  if (!Array.isArray(allFilters)) {
    allFilters = [];
  }

  if (
    isMapping &&
    mappingContent &&
    (mappingContent as { name?: string }).name === 'numPMIDsupportingAssociation'
  ) {
    const yFrom = (mappingContent as { selectedPmYearFrom?: number }).selectedPmYearFrom;
    const yTo = (mappingContent as { selectedPmYearTo?: number }).selectedPmYearTo;
    const hasYearRange =
      yFrom != null &&
      yTo != null &&
      Number.isFinite(Number(yFrom)) &&
      Number.isFinite(Number(yTo)) &&
      Number(yFrom) <= Number(yTo);

    if (hasYearRange) {
      allFilters = [
        ...allFilters.filter((f: any) => f?.name !== 'pmYear'),
        {
          name: 'pmYear',
          operator: 'gte',
          type: 'number',
          integer: true,
          value: String(Math.trunc(Number(yFrom))),
        },
        {
          name: 'pmYear',
          operator: 'lte',
          type: 'number',
          integer: true,
          value: String(Math.trunc(Number(yTo))),
        },
      ];
    } else {
      const y = (mappingContent as { selectedPmYear?: number }).selectedPmYear;
      if (y != null && Number.isFinite(Number(y))) {
        const yearStr = String(Number(y));
        allFilters = [
          ...allFilters.filter((f: any) => f?.name !== 'pmYear'),
          {
            name: 'pmYear',
            operator: 'eq',
            type: 'number',
            integer: true,
            value: yearStr,
          },
        ];
      }
    }
  }

  const selectedReferenceType = (
    mappingContent as { selectedReferenceType?: 'PMID' | 'NCTID' | 'ALL' } | undefined
  )?.selectedReferenceType;

  // idents to filter mappings
  // Entity list tabs (/all endpoints) often use *_filter param (e.g. disease_filter, gene_ncbi_id_filter)
  let filterParam: FilterParam = null;
  filterParam = view === 'GENES' && tab === 'GENES' ? 'gene_ncbi_id_filter' : filterParam;
  filterParam = view === 'GENES' && tab !== 'GENES' ? 'gene_ncbi_id' : filterParam;
  filterParam = view === 'DISEASES' && tab === 'DISEASES' ? 'disease_filter' : filterParam;
  filterParam = view === 'DISEASES' && tab !== 'DISEASES' ? 'disease' : filterParam;
  filterParam = view === 'VARIANTS' && tab === 'VARIANTS' ? 'variant_name' : filterParam;
  filterParam = view === 'VARIANTS' && tab !== 'VARIANTS' ? 'variant' : filterParam;
  filterParam = view === 'CHEMICALS' && tab === 'CHEMICALS' ? 'chem_ids_filter' : filterParam;
  filterParam = view === 'CHEMICALS' && tab !== 'CHEMICALS' ? 'chem_ids_init' : filterParam;
  filterParam = view === 'ALL' ? null : filterParam;

  // The backend idents value:
  let backendDiseaseVocabPrefix = filterParam === 'disease' ? 'UMLS_' : '';
  const filterValue = searchItems!.map(item => backendDiseaseVocabPrefix + item.id).join(',');

  // order mapping
  if (order === 1) {
    order = 'asc';
  } else if (order === -1) {
    order = 'desc';
  }

  // Update orderByParam with custom mappings
  const specialSortParams = [
    'numGenesAssociatedToDisease',
    'numVariantsAssociatedToDisease',
    'numVariantsAssociatedToGene',
    'numDiseasesAssociatedToGene',
    'numDiseasesAssociatedToVariant',
    'numDBSNPsupportingAssociation',
    'numPublications',
  ];
  if (specialSortParams.includes(orderByParam)) {
    orderByParam = `${orderByParam}_${source}`;
  }
  if (['yearInitial', 'yearFinal'].includes(orderByParam) && (tab === 'GDA' || tab === 'VDA')) {
    orderByParam = orderByParam + '_' + source;
  }

  // Handle specific naming inconsistencies or mappings
  switch (orderByParam) {
    case 'prevalence':
      orderByParam = 'disease_prevalence_class';
      break;
    case 'chemical_effect':
      orderByParam = 'tox_ther';
      break;
    case 'diseaseClassName':
      orderByParam = orderByParam.replace('_', '');
      break;
    case 'ddaRelation':
      orderByParam = 'rela';
      break;
    case 'symbol':
      orderByParam = 'symbol_keyword';
      break;
    case 'chemsInSentence':
      orderByParam = 'chemPrefName';
      break;
    default:
      break;
  }

  // Build params
  const expandedSource = source ? expandSourceValue(source, tab) : '';

  const paramsMap: Record<string, string | string[] | number | undefined> = {
    source: expandedSource,
    page_number: String(pageNum),
    page_size: String(pageSize),
    order_by: orderByParam ?? '',
    order: order != null ? String(order) : '',
  };

  // Apply user selected filters
  if (isMapping && mappingContent) {
    if (mappingContent.mappingItems) {
      mappingContent.mappingItems.forEach(item => {
        backendDiseaseVocabPrefix = item.filterParam === 'disease' ? 'UMLS_' : '';
        if (item.filterParam && paramsMap[item.filterParam]) {
          paramsMap[item.filterParam] += ',' + backendDiseaseVocabPrefix + item.filterValue;
        } else if (item.filterParam) {
          paramsMap[item.filterParam] = backendDiseaseVocabPrefix + item.filterValue;
        }
      });
    } else {
      return null;
    }
  } else {
    paramsMap[filterParam as string] = filterValue;
  }

  if (
    isMapping &&
    mappingContent &&
    (mappingContent as { name?: string }).name === 'numPMIDsupportingAssociation'
  ) {
    if (selectedReferenceType === 'PMID' || selectedReferenceType === 'NCTID') {
      paramsMap.reference_type = selectedReferenceType;
      allFilters = allFilters.filter((f: any) => f?.name !== 'reference_type');
    } else if (selectedReferenceType === 'ALL') {
      delete paramsMap.reference_type;
      allFilters = allFilters.filter((f: any) => f?.name !== 'reference_type');
    }
  }

  allFilters.forEach(filter => {
    const isStringOrSelect =
      filter.type === undefined || ['string', 'select'].includes(filter.type as string);
    if (filter.value && isStringOrSelect) {
      const map = getFiltersMap().get(filter.name) ?? filter.name;
      let filterValue = filter.value;

      if (filter.name === 'source') {
        filterValue = expandSourceValue(filter.value, tab);
      }

      if (filter.operator === 'contains') {
        if (Array.isArray(filterValue)) {
          filterValue = filterValue.map(v => `contains_${String(v)}`);
        } else if (typeof filterValue === 'string') {
          filterValue = `contains_${filterValue}`;
        }
      }

      paramsMap[map] = filterValue;
    }
  });

  // Apply number filters from ReactDataGrid
  const numericFilters = buildNumericFilters(allFilters);
  Object.assign(paramsMap, numericFilters);

  const minPm = paramsMap.min_pmYear != null ? Number(paramsMap.min_pmYear) : NaN;
  const maxPm = paramsMap.max_pmYear != null ? Number(paramsMap.max_pmYear) : NaN;
  if (Number.isFinite(minPm) && Number.isFinite(maxPm) && minPm !== maxPm && paramsMap.pmYear !== undefined) {
    delete paramsMap.pmYear;
  }

  // Construct URL params
  const params = new URLSearchParams();
  Object.entries(paramsMap)
    .filter(([_param, value]) => value !== (null as unknown) && value !== ('' as unknown))
    .filter(([param]) => param !== ('null' as unknown))
    .forEach(([param, value]) => {
      if (param === 'gene_symbol') {
        const values = Array.isArray(value)
          ? (value as string[]).map((v: string) => String(v).trim()).filter(Boolean)
          : String(value)
              .split(',')
              .map((v: string) => v.trim())
              .filter(Boolean);
        if (values.length === 0) return;
        const joined = values.join(',');
        const last = values[values.length - 1];
        const valueForParam = values.length > 1 && last ? `${joined},${last}` : joined;
        params.set(param, valueForParam);
        return;
      }
      const strVal = String(value);
      if (param === 'chem_ids_filter' && strVal.includes(',')) {
        strVal.split(',').forEach(id => params.append(param, id.trim()));
      } else {
        params.set(param, strVal);
      }
    });

  // Construct endpoint URL
  const baseEndpoint = buildBaseEndpoint({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
  });

  let endPoint = `${baseEndpoint}?${params.toString()}`;

  if (sourceWeights && sourceWeights.length > 0) {
    const weightsString = sourceWeights.map(sw => `${sw.name}:${sw.score}`).join(',');
    endPoint += `&source_weights=${weightsString}`;

    const hasSourceFilter = allFilters.some(filter => filter.name === 'source' && filter.value);

    if (sourceWeights.length > 1 && !hasSourceFilter) {
      endPoint = endPoint.replace(/([?&])source=[^&]*/, '$1source=ALL');
    }
  }

  return endPoint;
};

export default buildResultEndpoint;
